<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import FormTitle from '@js/components/forms/FormTitle.vue';
import SortableList from '@js/components/lists/SortableList.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useTabs } from '@js/composables/tabs';
import { formatPrice } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { isHttpUrl } from '@js/utils/string';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { boolean, number, object, string } from 'yup';

const {
    tenant,
    tenantTypes,
    seoPackages,
    seaPackages,
    smoPackages,
    smaPackages,
    semPackages,
    devPackages,
    hostingPackages,
    outsourcingPackages,
    cloudflarePackages,
    emailingPackages,
    shoppingfeedPackages,
    performanceStagePackages,
    daysPackages,
    specialPackages,
    moduleTree,
} = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    tenantTypes: {
        type: Array,
        required: true,
    },
    seoPackages: {
        type: Array,
        required: true,
    },
    seaPackages: {
        type: Array,
        required: true,
    },
    smoPackages: {
        type: Array,
        required: true,
    },
    smaPackages: {
        type: Array,
        required: true,
    },
    semPackages: {
        type: Array,
        required: true,
    },
    devPackages: {
        type: Array,
        required: true,
    },
    hostingPackages: {
        type: Array,
        required: true,
    },
    outsourcingPackages: {
        type: Array,
        required: true,
    },
    cloudflarePackages: {
        type: Array,
        required: true,
    },
    emailingPackages: {
        type: Array,
        required: true,
    },
    shoppingfeedPackages: {
        type: Array,
        required: true,
    },
    performanceStagePackages: {
        type: Array,
        required: true,
    },
    daysPackages: {
        type: Array,
        required: true,
    },
    specialPackages: {
        type: Array,
        required: true,
    },
    moduleTree: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();
const { visitPreservingTabs } = useTabs();

const isSubmitting = ref(false);

let roadmapUidCounter = 0;

function nextRoadmapUid() {
    return `${++roadmapUidCounter}`;
}

let contactUidCounter = 0;

function nextContactUid() {
    return `${++contactUidCounter}`;
}

const form = reactive({
    type: tenant
        ? tenantTypes.find((type) => type.value === tenant.type.value)
        : tenantTypes[0],
    name: tenant ? tenant.name : '',
    url: tenant ? tenant.url : '',
    isArchived: tenant ? tenant.isArchived : false,
    displayOnDashboard: tenant ? tenant.displayOnDashboard : true,
    hasInvoicing: tenant ? tenant.hasInvoicing : true,
    hasOpportunity: tenant ? tenant.hasOpportunity : true,
    hasApikomContent: tenant ? tenant.hasApikomContent : true,
    hasContentCategories: tenant ? tenant.hasContentCategories : true,
    hasContentProducts: tenant ? tenant.hasContentProducts : true,
    hasIndexation: tenant ? tenant.hasIndexation : true,
    hasNetlinking: tenant ? tenant.hasNetlinking : true,
    hasPositionTracking: tenant ? tenant.hasPositionTracking : true,
    hasGoogleAdsAudience: tenant ? tenant.hasGoogleAdsAudience : true,
    hasFacebookAdsAudience: tenant ? tenant.hasFacebookAdsAudience : true,
    hasTenantTask: tenant ? tenant.hasTenantTask : true,
    contacts:
        tenant && tenant.contacts && tenant.contacts.length > 0
            ? tenant.contacts.map((contact) => ({
                  ...contact,
                  _uid: nextContactUid(),
              }))
            : [],
    roadmapObjectives:
        tenant && tenant.roadmapObjectives
            ? tenant.roadmapObjectives.map((objective) => ({
                  ...objective,
                  _uid: nextRoadmapUid(),
              }))
            : [],
    serviceStartDate: tenant ? tenant.serviceStartDate : '',
    serviceEndDate: tenant ? tenant.serviceEndDate : '',
    serviceNotes: tenant ? tenant.serviceNotes : '',
    displayTimeEveryMondayOfTheCurrentMonth: tenant
        ? tenant.displayTimeEveryMondayOfTheCurrentMonth
        : false,
    displayTimeOfTheCurrentMonth: tenant
        ? tenant.displayTimeOfTheCurrentMonth
        : false,
    seoPackage:
        tenant && tenant.seoPackage
            ? seoPackages.find(
                  (seoPackage) => seoPackage.id === tenant.seoPackage.id,
              )
            : null,
    seaPackage:
        tenant && tenant.seaPackage
            ? seaPackages.find(
                  (seaPackage) => seaPackage.id === tenant.seaPackage.id,
              )
            : null,
    smoPackage:
        tenant && tenant.smoPackage
            ? smoPackages.find(
                  (smoPackage) => smoPackage.id === tenant.smoPackage.id,
              )
            : null,
    smaPackage:
        tenant && tenant.smaPackage
            ? smaPackages.find(
                  (smaPackage) => smaPackage.id === tenant.smaPackage.id,
              )
            : null,
    semPackage:
        tenant && tenant.semPackage
            ? semPackages.find(
                  (semPackage) => semPackage.id === tenant.semPackage.id,
              )
            : null,
    devPackage:
        tenant && tenant.devPackage
            ? devPackages.find(
                  (devPackage) => devPackage.id === tenant.devPackage.id,
              )
            : null,
    hostingPackage:
        tenant && tenant.hostingPackage
            ? hostingPackages.find(
                  (hostingPackage) =>
                      hostingPackage.id === tenant.hostingPackage.id,
              )
            : null,
    outsourcingPackage:
        tenant && tenant.outsourcingPackage
            ? outsourcingPackages.find(
                  (outsourcingPackage) =>
                      outsourcingPackage.id === tenant.outsourcingPackage.id,
              )
            : null,
    cloudflarePackage:
        tenant && tenant.cloudflarePackage
            ? cloudflarePackages.find(
                  (cloudflarePackage) =>
                      cloudflarePackage.id === tenant.cloudflarePackage.id,
              )
            : null,
    emailingPackage:
        tenant && tenant.emailingPackage
            ? emailingPackages.find(
                  (emailingPackage) =>
                      emailingPackage.id === tenant.emailingPackage.id,
              )
            : null,
    shoppingfeedPackage:
        tenant && tenant.shoppingfeedPackage
            ? shoppingfeedPackages.find(
                  (shoppingfeedPackage) =>
                      shoppingfeedPackage.id === tenant.shoppingfeedPackage.id,
              )
            : null,
    performanceStagePackage:
        tenant && tenant.performanceStagePackage
            ? performanceStagePackages.find(
                  (performanceStagePackage) =>
                      performanceStagePackage.id ===
                      tenant.performanceStagePackage.id,
              )
            : null,
    daysPackage:
        tenant && tenant.daysPackage
            ? daysPackages.find(
                  (daysPackage) => daysPackage.id === tenant.daysPackage.id,
              )
            : null,
    specialPackage:
        tenant && tenant.specialPackage
            ? specialPackages.find(
                  (specialPackage) =>
                      specialPackage.id === tenant.specialPackage.id,
              )
            : null,
    seoHoursPerMonth: tenant ? tenant.seoHoursPerMonth : null,
    seoNetlinkingBudgetPerMonth: tenant
        ? tenant.seoNetlinkingBudgetPerMonth
        : null,
    seaHoursPerMonth: tenant ? tenant.seaHoursPerMonth : null,
    smoHoursPerMonth: tenant ? tenant.smoHoursPerMonth : null,
    smaHoursPerMonth: tenant ? tenant.smaHoursPerMonth : null,
    emailing: tenant ? tenant.emailing : false,
    dev: tenant ? tenant.dev : false,
    devCoefficient: tenant ? tenant.devCoefficient : 1,
    freeCloudflare: tenant ? tenant.freeCloudflare : false,
    proCloudflare: tenant ? tenant.proCloudflare : false,
    service: tenant ? tenant.service : '',
    internalNotes: tenant ? tenant.internalNotes : '',
    seaBudgetPerMonth: tenant ? tenant.seaBudgetPerMonth : 0,
    smaBudgetPerMonth: tenant ? tenant.smaBudgetPerMonth : 0,
    maxBudgetPerMonth: tenant ? tenant.maxBudgetPerMonth : 0,
    roasTargetPercentage: tenant ? tenant.roasTargetPercentage : 0,
    grossMarginPercentage: tenant ? tenant.grossMarginPercentage : 0,
    budgetNotes: tenant ? tenant.budgetNotes : '',
    seoNotesToDo: tenant ? tenant.seoNotesToDo : '',
    seoNotesLastChanges: tenant ? tenant.seoNotesLastChanges : '',
    seaNotesToDo: tenant ? tenant.seaNotesToDo : '',
    seaNotesLastChanges: tenant ? tenant.seaNotesLastChanges : '',
    seaNotesOtherInformation: tenant ? tenant.seaNotesOtherInformation : '',
    seaNotesBudget: tenant ? tenant.seaNotesBudget : '',
    smaNotesToDo: tenant ? tenant.smaNotesToDo : '',
    smaNotesLastChanges: tenant ? tenant.smaNotesLastChanges : '',
    smoNotesToDo: tenant ? tenant.smoNotesToDo : '',
    smoNotesLastChanges: tenant ? tenant.smoNotesLastChanges : '',
    moduleTree: moduleTree,
    numberOfStandardContentsPerMonth: tenant
        ? tenant.numberOfStandardContentsPerMonth
        : null,
    numberOfPremiumContentsPerMonth: tenant
        ? tenant.numberOfPremiumContentsPerMonth
        : null,
    numberOfMetasPerMonth: tenant ? tenant.numberOfMetasPerMonth : null,
    numberOfBannersPerMonth: tenant ? tenant.numberOfBannersPerMonth : null,
    numberOfEmailingPerMonth: tenant ? tenant.numberOfEmailingPerMonth : null,
    numberOfShortMessageServicesPerMonth: tenant
        ? tenant.numberOfShortMessageServicesPerMonth
        : null,
    numberOfTechnicalAuditPerMonth: tenant
        ? tenant.numberOfTechnicalAuditPerMonth
        : null,
    numberOfGoogleSearchConsolePerMonth: tenant
        ? tenant.numberOfGoogleSearchConsolePerMonth
        : null,
    numberOfGoogleMyBusinessPerMonth: tenant
        ? tenant.numberOfGoogleMyBusinessPerMonth
        : null,
    numberOfSeoAiAnalysisPerMonth: tenant
        ? tenant.numberOfSeoAiAnalysisPerMonth
        : null,
    numberOfSeaCampaignOptimizationPerMonth: tenant
        ? tenant.numberOfSeaCampaignOptimizationPerMonth
        : null,
    numberOfSeaGoogleAdsQualityPerMonth: tenant
        ? tenant.numberOfSeaGoogleAdsQualityPerMonth
        : null,
    numberOfSeaMerchantCenterHealthPerMonth: tenant
        ? tenant.numberOfSeaMerchantCenterHealthPerMonth
        : null,
    numberOfSeaAiAnalysisPerMonth: tenant
        ? tenant.numberOfSeaAiAnalysisPerMonth
        : null,
    numberOfSmaCampaignOptimizationPerMonth: tenant
        ? tenant.numberOfSmaCampaignOptimizationPerMonth
        : null,
    numberOfSmaAccountQualityPerMonth: tenant
        ? tenant.numberOfSmaAccountQualityPerMonth
        : null,
    numberOfSmaShoppingFeedHealthPerMonth: tenant
        ? tenant.numberOfSmaShoppingFeedHealthPerMonth
        : null,
    numberOfSmaAiAnalysisPerMonth: tenant
        ? tenant.numberOfSmaAiAnalysisPerMonth
        : null,
    numberOfEditorialFrameworkPerMonth: tenant
        ? tenant.numberOfEditorialFrameworkPerMonth
        : null,
    numberOfSmoAiAnalysisPerMonth: tenant
        ? tenant.numberOfSmoAiAnalysisPerMonth
        : null,
    hasEmailing: tenant ? tenant.hasEmailing : false,
    hasShortMessageService: tenant ? tenant.hasShortMessageService : false,
    hasPageSpeed: tenant ? tenant.hasPageSpeed : false,
    hasEmailingAutomation: tenant ? tenant.hasEmailingAutomation : false,
    hasCompetitors: tenant ? tenant.hasCompetitors : false,
    alertCategoryWithLessThanXProductsInStock: tenant
        ? tenant.alertCategoryWithLessThanXProductsInStock
        : null,
    displayApikomTaskTimeFrom: tenant ? tenant.displayApikomTaskTimeFrom : null,
    numberOfVisibleReportMonths: tenant
        ? tenant.numberOfVisibleReportMonths
        : 2,
    numberOfVisibleReportQuarters: tenant
        ? tenant.numberOfVisibleReportQuarters
        : 4,
});

const baseValidationSchema = object({
    type: object().required('Le type est requis'),
    name: string().required('Le nom est requis'),
    url: string()
        .required("L'URL est requise")
        .test('url', "L'URL doit être valide", isHttpUrl),
    isArchived: boolean().required('Le statut archivé est requis'),
    displayOnDashboard: boolean().required(
        'Le statut affichage sur le dashboard est requis',
    ),
    hasInvoicing: boolean().nullable(),
    hasOpportunity: boolean().nullable(),
    hasApikomContent: boolean().nullable(),
    hasContentCategories: boolean().nullable(),
    hasContentProducts: boolean().nullable(),
    hasIndexation: boolean().nullable(),
    hasNetlinking: boolean().nullable(),
    hasPositionTracking: boolean().nullable(),
    hasGoogleAdsAudience: boolean().nullable(),
    hasFacebookAdsAudience: boolean().nullable(),
    hasTenantTask: boolean().nullable(),
    serviceStartDate: string().nullable(),
    serviceEndDate: string().nullable(),
    serviceNotes: string().nullable(),
    displayTimeEveryMondayOfTheCurrentMonth: boolean().nullable(),
    displayTimeOfTheCurrentMonth: boolean().nullable(),
    seoPackage: object().nullable(),
    seaPackage: object().nullable(),
    smoPackage: object().nullable(),
    smaPackage: object().nullable(),
    semPackage: object().nullable(),
    devPackage: object().nullable(),
    hostingPackage: object().nullable(),
    outsourcingPackage: object().nullable(),
    cloudflarePackage: object().nullable(),
    emailingPackage: object().nullable(),
    shoppingfeedPackage: object().nullable(),
    performanceStagePackage: object().nullable(),
    daysPackage: object().nullable(),
    specialPackage: object().nullable(),
    seoHoursPerMonth: string().nullable(),
    seoNetlinkingBudgetPerMonth: string().nullable(),
    seaHoursPerMonth: string().nullable(),
    smoHoursPerMonth: string().nullable(),
    smaHoursPerMonth: string().nullable(),
    emailing: boolean().nullable(),
    dev: boolean().nullable(),
    devCoefficient: number().nullable(),
    freeCloudflare: boolean().nullable(),
    proCloudflare: boolean().nullable(),
    service: string().nullable(),
    internalNotes: string().nullable(),
    seaBudgetPerMonth: number().nullable(),
    smaBudgetPerMonth: number().nullable(),
    maxBudgetPerMonth: number().nullable(),
    roasTargetPercentage: number().nullable(),
    grossMarginPercentage: number().nullable(),
    budgetNotes: string().nullable(),
    seoNotesToDo: string().nullable(),
    seoNotesLastChanges: string().nullable(),
    seaNotesToDo: string().nullable(),
    seaNotesLastChanges: string().nullable(),
    seaNotesOtherInformation: string().nullable(),
    seaNotesBudget: string().nullable(),
    smaNotesToDo: string().nullable(),
    smaNotesLastChanges: string().nullable(),
    smoNotesToDo: string().nullable(),
    smoNotesLastChanges: string().nullable(),
    numberOfStandardContentsPerMonth: number().nullable(),
    numberOfPremiumContentsPerMonth: number().nullable(),
    numberOfMetasPerMonth: number().nullable(),
    numberOfBannersPerMonth: number().nullable(),
    numberOfEmailingPerMonth: number().nullable(),
    numberOfShortMessageServicesPerMonth: number().nullable(),
    numberOfTechnicalAuditPerMonth: number().nullable(),
    numberOfGoogleSearchConsolePerMonth: number().nullable(),
    numberOfGoogleMyBusinessPerMonth: number().nullable(),
    numberOfSeoAiAnalysisPerMonth: number().nullable(),
    numberOfSeaCampaignOptimizationPerMonth: number().nullable(),
    numberOfSeaGoogleAdsQualityPerMonth: number().nullable(),
    numberOfSeaMerchantCenterHealthPerMonth: number().nullable(),
    numberOfSeaAiAnalysisPerMonth: number().nullable(),
    numberOfSmaCampaignOptimizationPerMonth: number().nullable(),
    numberOfSmaAccountQualityPerMonth: number().nullable(),
    numberOfSmaShoppingFeedHealthPerMonth: number().nullable(),
    numberOfSmaAiAnalysisPerMonth: number().nullable(),
    numberOfEditorialFrameworkPerMonth: number().nullable(),
    numberOfSmoAiAnalysisPerMonth: number().nullable(),
    hasEmailing: boolean().nullable(),
    hasShortMessageService: boolean().nullable(),
    hasPageSpeed: boolean().nullable(),
    hasEmailingAutomation: boolean().nullable(),
    hasCompetitors: boolean().nullable(),
    alertCategoryWithLessThanXProductsInStock: number().nullable(),
    displayApikomTaskTimeFrom: string().nullable(),
    numberOfVisibleReportMonths: number().nullable(),
    numberOfVisibleReportQuarters: number().nullable(),
});

const validationSchema = computed(() => {
    const dynamicSchema = {};

    form.contacts.forEach((contact) => {
        dynamicSchema[`contactName_${contact._uid}`] = string()
            .nullable()
            .test(
                'contactHasAtLeastOneFilledField',
                "Renseignez au moins le nom, l'email ou le téléphone du contact",
                function () {
                    return [
                        `contactName_${contact._uid}`,
                        `contactEmail_${contact._uid}`,
                        `contactPhone_${contact._uid}`,
                    ].some((fieldName) =>
                        String(this.parent[fieldName] ?? '').trim(),
                    );
                },
            );
        dynamicSchema[`contactEmail_${contact._uid}`] = string()
            .email("L'email doit être valide")
            .nullable();
        dynamicSchema[`contactPhone_${contact._uid}`] = string().nullable();
    });

    form.roadmapObjectives.forEach((objective) => {
        dynamicSchema[`roadmapObjectiveTitle_${objective._uid}`] =
            string().required("L'intitulé de l'objectif est requis");
    });

    return baseValidationSchema.shape(dynamicSchema);
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.postCustomer',
                tenant ? { tenant: tenant.id } : {},
            ),
            {
                type: form.type.value,
                name: form.name,
                url: form.url,
                isArchived: form.isArchived,
                displayOnDashboard: form.displayOnDashboard,
                hasInvoicing: form.hasInvoicing,
                hasOpportunity: form.hasOpportunity,
                hasApikomContent: form.hasApikomContent,
                hasContentCategories: form.hasContentCategories,
                hasContentProducts: form.hasContentProducts,
                hasIndexation: form.hasIndexation,
                hasNetlinking: form.hasNetlinking,
                hasPositionTracking: form.hasPositionTracking,
                hasGoogleAdsAudience: form.hasGoogleAdsAudience,
                hasFacebookAdsAudience: form.hasFacebookAdsAudience,
                hasTenantTask: form.hasTenantTask,
                contacts: form.contacts.map((contact) => ({
                    id: contact.id,
                    name: contact.name,
                    email: contact.email,
                    phone: contact.phone,
                })),
                roadmapObjectives: form.roadmapObjectives.map((objective) => ({
                    id: objective.id,
                    title: objective.title,
                    description: objective.description,
                })),
                serviceStartDate: form.serviceStartDate,
                serviceEndDate: form.serviceEndDate,
                serviceNotes: form.serviceNotes,
                displayTimeEveryMondayOfTheCurrentMonth:
                    form.displayTimeEveryMondayOfTheCurrentMonth,
                displayTimeOfTheCurrentMonth: form.displayTimeOfTheCurrentMonth,
                seoPackageId: form.seoPackage ? form.seoPackage.id : null,
                seaPackageId: form.seaPackage ? form.seaPackage.id : null,
                smoPackageId: form.smoPackage ? form.smoPackage.id : null,
                smaPackageId: form.smaPackage ? form.smaPackage.id : null,
                semPackageId: form.semPackage ? form.semPackage.id : null,
                devPackageId: form.devPackage ? form.devPackage.id : null,
                hostingPackageId: form.hostingPackage
                    ? form.hostingPackage.id
                    : null,
                outsourcingPackageId: form.outsourcingPackage
                    ? form.outsourcingPackage.id
                    : null,
                cloudflarePackageId: form.cloudflarePackage
                    ? form.cloudflarePackage.id
                    : null,
                emailingPackageId: form.emailingPackage
                    ? form.emailingPackage.id
                    : null,
                shoppingfeedPackageId: form.shoppingfeedPackage
                    ? form.shoppingfeedPackage.id
                    : null,
                performanceStagePackageId: form.performanceStagePackage
                    ? form.performanceStagePackage.id
                    : null,
                daysPackageId: form.daysPackage ? form.daysPackage.id : null,
                specialPackageId: form.specialPackage
                    ? form.specialPackage.id
                    : null,
                seoHoursPerMonth: form.seoHoursPerMonth,
                seoNetlinkingBudgetPerMonth: form.seoNetlinkingBudgetPerMonth,
                seaHoursPerMonth: form.seaHoursPerMonth,
                smoHoursPerMonth: form.smoHoursPerMonth,
                smaHoursPerMonth: form.smaHoursPerMonth,
                emailing: form.emailing,
                dev: form.dev,
                devCoefficient: form.devCoefficient,
                freeCloudflare: form.freeCloudflare,
                proCloudflare: form.proCloudflare,
                service: form.service,
                internalNotes: form.internalNotes,
                seaBudgetPerMonth: form.seaBudgetPerMonth,
                smaBudgetPerMonth: form.smaBudgetPerMonth,
                maxBudgetPerMonth: form.maxBudgetPerMonth,
                roasTargetPercentage: form.roasTargetPercentage,
                grossMarginPercentage: form.grossMarginPercentage,
                budgetNotes: form.budgetNotes,
                seoNotesToDo: form.seoNotesToDo,
                seoNotesLastChanges: form.seoNotesLastChanges,
                seaNotesToDo: form.seaNotesToDo,
                seaNotesLastChanges: form.seaNotesLastChanges,
                seaNotesOtherInformation: form.seaNotesOtherInformation,
                seaNotesBudget: form.seaNotesBudget,
                smaNotesToDo: form.smaNotesToDo,
                smaNotesLastChanges: form.smaNotesLastChanges,
                smoNotesToDo: form.smoNotesToDo,
                smoNotesLastChanges: form.smoNotesLastChanges,
                moduleTree: form.moduleTree,
                numberOfStandardContentsPerMonth:
                    form.numberOfStandardContentsPerMonth,
                numberOfPremiumContentsPerMonth:
                    form.numberOfPremiumContentsPerMonth,
                numberOfMetasPerMonth: form.numberOfMetasPerMonth,
                numberOfBannersPerMonth: form.numberOfBannersPerMonth,
                numberOfEmailingPerMonth: form.numberOfEmailingPerMonth,
                numberOfShortMessageServicesPerMonth:
                    form.numberOfShortMessageServicesPerMonth,
                numberOfTechnicalAuditPerMonth:
                    form.numberOfTechnicalAuditPerMonth,
                numberOfGoogleSearchConsolePerMonth:
                    form.numberOfGoogleSearchConsolePerMonth,
                numberOfGoogleMyBusinessPerMonth:
                    form.numberOfGoogleMyBusinessPerMonth,
                numberOfSeoAiAnalysisPerMonth:
                    form.numberOfSeoAiAnalysisPerMonth,
                numberOfSeaCampaignOptimizationPerMonth:
                    form.numberOfSeaCampaignOptimizationPerMonth,
                numberOfSeaGoogleAdsQualityPerMonth:
                    form.numberOfSeaGoogleAdsQualityPerMonth,
                numberOfSeaMerchantCenterHealthPerMonth:
                    form.numberOfSeaMerchantCenterHealthPerMonth,
                numberOfSeaAiAnalysisPerMonth:
                    form.numberOfSeaAiAnalysisPerMonth,
                numberOfSmaCampaignOptimizationPerMonth:
                    form.numberOfSmaCampaignOptimizationPerMonth,
                numberOfSmaAccountQualityPerMonth:
                    form.numberOfSmaAccountQualityPerMonth,
                numberOfSmaShoppingFeedHealthPerMonth:
                    form.numberOfSmaShoppingFeedHealthPerMonth,
                numberOfSmaAiAnalysisPerMonth:
                    form.numberOfSmaAiAnalysisPerMonth,
                numberOfEditorialFrameworkPerMonth:
                    form.numberOfEditorialFrameworkPerMonth,
                numberOfSmoAiAnalysisPerMonth:
                    form.numberOfSmoAiAnalysisPerMonth,
                hasEmailing: form.hasEmailing,
                hasShortMessageService: form.hasShortMessageService,
                hasPageSpeed: form.hasPageSpeed,
                hasEmailingAutomation: form.hasEmailingAutomation,
                hasCompetitors: form.hasCompetitors,
                alertCategoryWithLessThanXProductsInStock:
                    form.alertCategoryWithLessThanXProductsInStock,
                displayApikomTaskTimeFrom: form.displayApikomTaskTimeFrom,
                numberOfVisibleReportMonths: form.numberOfVisibleReportMonths,
                numberOfVisibleReportQuarters:
                    form.numberOfVisibleReportQuarters,
            },
        )
        .then((response) => {
            toastSaved();

            visitPreservingTabs(
                route('front.projectManagement.customer.updateCustomer', {
                    tenant: response.data.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);
            router.visit(route('front.projectManagement.customer.contacts'));
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Information',
                    contentSlotName: 'information',
                },
                services: {
                    title: 'Prestations',
                    contentSlotName: 'services',
                },
                budgets: {
                    title: 'Budgets',
                    contentSlotName: 'budgets',
                },
                apikomObjectives: {
                    title: 'Objectifs Apikom',
                    contentSlotName: 'apikomObjectives',
                },
                roadmap: {
                    title: 'Feuille de route',
                    contentSlotName: 'roadmap',
                },
                notes: {
                    title: 'Notes',
                    items: {
                        internalNotes: {
                            title: 'Internes',
                            contentSlotName: 'internalNotes',
                        },
                        seoNotes: {
                            title: 'SEO/GEO',
                            contentSlotName: 'seoNotes',
                        },
                        seaNotes: {
                            title: 'SEA',
                            contentSlotName: 'seaNotes',
                        },
                        smaNotes: {
                            title: 'SMA',
                            contentSlotName: 'smaNotes',
                        },
                        smoNotes: {
                            title: 'SMO',
                            contentSlotName: 'smoNotes',
                        },
                    },
                },
                display: {
                    title: 'Affichage',
                    contentSlotName: 'display',
                },
                modules: {
                    title: 'Modules',
                    contentSlotName: 'modules',
                },
                alerts: {
                    title: 'Alertes',
                    contentSlotName: 'alerts',
                },
            }"
            :unmount-on-hide="false"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="3">
                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="tenantTypes"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <InputField
                                v-model="form.name"
                                label="Nom"
                                field-name="name"
                                field-id="name"
                                required
                            />

                            <InputField
                                v-model="form.url"
                                label="URL"
                                field-name="url"
                                field-id="url"
                                type="url"
                                required
                            />
                        </FormGrid>

                        <FormSeparator />

                        <FormGrid>
                            <SwitchField
                                v-model="form.isArchived"
                                label="Archivé"
                                field-name="isArchived"
                                field-id="isArchived"
                            />

                            <SwitchField
                                v-model="form.displayOnDashboard"
                                label="Afficher sur le tableau de bord"
                                field-name="displayOnDashboard"
                                field-id="displayOnDashboard"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <SortableList
                            v-model="form.contacts"
                            :sortable="false"
                            add-label="Ajouter un contact"
                            empty-text="Aucun contact pour le moment."
                            :new-item="
                                () => ({
                                    id: null,
                                    name: '',
                                    email: '',
                                    phone: '',
                                    _uid: nextContactUid(),
                                })
                            "
                        >
                            <template #default="{ item }">
                                <FormGrid :columns="3">
                                    <InputField
                                        v-model="item.name"
                                        label="Nom"
                                        :field-name="`contactName_${item._uid}`"
                                        :field-id="`contactName_${item._uid}`"
                                        type="text"
                                    />

                                    <InputField
                                        v-model="item.email"
                                        label="Email"
                                        :field-name="`contactEmail_${item._uid}`"
                                        :field-id="`contactEmail_${item._uid}`"
                                        type="email"
                                    />

                                    <InputField
                                        v-model="item.phone"
                                        label="Téléphone"
                                        :field-name="`contactPhone_${item._uid}`"
                                        :field-id="`contactPhone_${item._uid}`"
                                        type="tel"
                                    />
                                </FormGrid>
                            </template>
                        </SortableList>
                    </FormContent>
                </FormColumn>
            </template>

            <template #services>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="3">
                            <InputField
                                v-model="form.serviceStartDate"
                                label="Début de prestation"
                                field-name="serviceStartDate"
                                field-id="serviceStartDate"
                                type="date"
                            />

                            <InputField
                                v-model="form.serviceEndDate"
                                label="Fin de prestation"
                                field-name="serviceEndDate"
                                field-id="serviceEndDate"
                                type="date"
                            />

                            <InputField
                                v-model="form.displayApikomTaskTimeFrom"
                                label="Afficher le temps des tâches Apikom depuis"
                                field-name="displayApikomTaskTimeFrom"
                                field-id="displayApikomTaskTimeFrom"
                                type="date"
                            />

                            <InputField
                                v-model="form.numberOfVisibleReportMonths"
                                label="Nombre de mois de rapports visibles par le client"
                                field-name="numberOfVisibleReportMonths"
                                field-id="numberOfVisibleReportMonths"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfVisibleReportQuarters"
                                label="Nombre de trimestres de rapports visibles par le client"
                                field-name="numberOfVisibleReportQuarters"
                                field-id="numberOfVisibleReportQuarters"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.serviceNotes"
                            label="Notes prestation"
                            field-name="serviceNotes"
                            field-id="serviceNotes"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <FormGrid>
                            <ListboxField
                                v-model="form.daysPackage"
                                field-id="daysPackage"
                                field-name="daysPackage"
                                label="Forfait jours"
                                :options="daysPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.daysPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.performanceStagePackage"
                                field-id="performanceStagePackage"
                                field-name="performanceStagePackage"
                                label="Forfait palier/perf"
                                :options="performanceStagePackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.performanceStagePackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.specialPackage"
                                field-id="specialPackage"
                                field-name="specialPackage"
                                label="Forfait spécial"
                                :options="specialPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.specialPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>
                        </FormGrid>

                        <FormGrid>
                            <ListboxField
                                v-model="form.seoPackage"
                                field-id="seoPackage"
                                field-name="seoPackage"
                                label="Forfait SEO/GEO"
                                :options="seoPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.seoPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.seaPackage"
                                field-id="seaPackage"
                                field-name="seaPackage"
                                label="Forfait SEA"
                                :options="seaPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.seaPackage.name }} ({{
                                        formatPrice(
                                            form.seaPackage.price,
                                            false,
                                        )
                                    }})
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }} ({{
                                        formatPrice(option.price, false)
                                    }})
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.smoPackage"
                                field-id="smoPackage"
                                field-name="smoPackage"
                                label="Forfait SMO"
                                :options="smoPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.smoPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.smaPackage"
                                field-id="smaPackage"
                                field-name="smaPackage"
                                label="Forfait SMA"
                                :options="smaPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.smaPackage.name }} ({{
                                        formatPrice(
                                            form.smaPackage.price,
                                            false,
                                        )
                                    }})
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }} ({{
                                        formatPrice(option.price, false)
                                    }})
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.semPackage"
                                field-id="semPackage"
                                field-name="semPackage"
                                label="Forfait SEM"
                                :options="semPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.semPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.emailingPackage"
                                field-id="emailingPackage"
                                field-name="emailingPackage"
                                label="Forfait emailing"
                                :options="emailingPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.emailingPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.shoppingfeedPackage"
                                field-id="shoppingfeedPackage"
                                field-name="shoppingfeedPackage"
                                label="Forfait shopingfeed"
                                :options="shoppingfeedPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.shoppingfeedPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.devPackage"
                                field-id="devPackage"
                                field-name="devPackage"
                                label="Forfait DEV"
                                :options="devPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.devPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.hostingPackage"
                                field-id="hostingPackage"
                                field-name="hostingPackage"
                                label="Forfait hébergement"
                                :options="hostingPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.hostingPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.outsourcingPackage"
                                field-id="outsourcingPackage"
                                field-name="outsourcingPackage"
                                label="Forfait infogérance"
                                :options="outsourcingPackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.outsourcingPackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>

                            <ListboxField
                                v-model="form.cloudflarePackage"
                                field-id="cloudflarePackage"
                                field-name="cloudflarePackage"
                                label="Forfait Cloudflare"
                                :options="cloudflarePackages"
                                has-empty-option
                                by="id"
                            >
                                <template #buttonContent>
                                    {{ form.cloudflarePackage.name }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.name }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid>
                            <InputField
                                v-model="form.seoHoursPerMonth"
                                label="Heure SEO/mois"
                                field-name="seoHoursPerMonth"
                                field-id="seoHoursPerMonth"
                                type="number"
                                :step="1"
                                :min="0"
                            />

                            <InputField
                                v-model="form.seoNetlinkingBudgetPerMonth"
                                label="Budget netlinking SEO/mois"
                                field-name="seoNetlinkingBudgetPerMonth"
                                field-id="seoNetlinkingBudgetPerMonth"
                                type="number"
                                :step="0.01"
                                :min="0"
                            />

                            <InputField
                                v-model="form.seaHoursPerMonth"
                                label="Heures SEA/mois"
                                field-name="seaHoursPerMonth"
                                field-id="seaHoursPerMonth"
                                type="number"
                                :step="1"
                                :min="0"
                            />

                            <InputField
                                v-model="form.smoHoursPerMonth"
                                label="Heures SMO/mois"
                                field-name="smoHoursPerMonth"
                                field-id="smoHoursPerMonth"
                                type="number"
                                :step="1"
                                :min="0"
                            />

                            <InputField
                                v-model="form.smaHoursPerMonth"
                                label="Heures SMA/mois"
                                field-name="smaHoursPerMonth"
                                field-id="smaHoursPerMonth"
                                type="number"
                                :step="1"
                                :min="0"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid>
                            <SwitchField
                                v-model="form.emailing"
                                label="Emailing"
                                field-name="emailing"
                                field-id="emailing"
                            />

                            <SwitchField
                                v-model="form.dev"
                                label="DEV"
                                field-name="dev"
                                field-id="dev"
                            />

                            <SwitchField
                                v-model="form.freeCloudflare"
                                label="Cloudflare gratuit"
                                field-name="freeCloudflare"
                                field-id="freeCloudflare"
                            />

                            <SwitchField
                                v-model="form.proCloudflare"
                                label="Cloudflare Pro"
                                field-name="proCloudflare"
                                field-id="proCloudflare"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid>
                            <InputField
                                v-model="form.devCoefficient"
                                label="Coef DEV"
                                field-name="devCoefficient"
                                field-id="devCoefficient"
                                type="number"
                                :step="0.01"
                                :min="0"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #budgets>
                <FormColumn>
                    <FormContent>
                        <FormGrid>
                            <InputField
                                v-model="form.seaBudgetPerMonth"
                                label="Budget SEA/mois"
                                field-name="seaBudgetPerMonth"
                                field-id="seaBudgetPerMonth"
                                type="number"
                                min="0"
                                step="0.01"
                            />

                            <InputField
                                v-model="form.smaBudgetPerMonth"
                                label="Budget SMA/mois"
                                field-name="smaBudgetPerMonth"
                                field-id="smaBudgetPerMonth"
                                type="number"
                                min="0"
                                step="0.01"
                            />

                            <InputField
                                v-model="form.maxBudgetPerMonth"
                                label="Budget max/mois"
                                field-name="maxBudgetPerMonth"
                                field-id="maxBudgetPerMonth"
                                type="number"
                                min="0"
                                step="0.01"
                            />
                        </FormGrid>

                        <FormSeparator />

                        <FormGrid>
                            <InputField
                                v-model="form.roasTargetPercentage"
                                label="Pub / CA %"
                                field-name="roasTargetPercentage"
                                field-id="roasTargetPercentage"
                                type="number"
                                min="0"
                                step="0.01"
                            />
                        </FormGrid>

                        <FormSeparator />

                        <FormGrid>
                            <InputField
                                v-model="form.grossMarginPercentage"
                                label="Marge brute %"
                                field-name="grossMarginPercentage"
                                field-id="grossMarginPercentage"
                                type="number"
                                min="0"
                                step="0.01"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.budgetNotes"
                            label="Notes budget"
                            field-name="budgetNotes"
                            field-id="budgetNotes"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #apikomObjectives>
                <FormColumn>
                    <FormContent>
                        <FormTitle> SEO/GEO </FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="form.numberOfStandardContentsPerMonth"
                                label="Contenus standard/mois"
                                field-name="numberOfStandardContentsPerMonth"
                                field-id="numberOfStandardContentsPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfPremiumContentsPerMonth"
                                label="Contenus premium/mois"
                                field-name="numberOfPremiumContentsPerMonth"
                                field-id="numberOfPremiumContentsPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfMetasPerMonth"
                                label="Meta/mois"
                                field-name="numberOfMetasPerMonth"
                                field-id="numberOfMetasPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfTechnicalAuditPerMonth"
                                label="Analyses techniques/mois"
                                field-name="numberOfTechnicalAuditPerMonth"
                                field-id="numberOfTechnicalAuditPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="
                                    form.numberOfGoogleSearchConsolePerMonth
                                "
                                label="Analyse Search Console/mois"
                                field-name="numberOfGoogleSearchConsolePerMonth"
                                field-id="numberOfGoogleSearchConsolePerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfGoogleMyBusinessPerMonth"
                                label="Google My Business/mois"
                                field-name="numberOfGoogleMyBusinessPerMonth"
                                field-id="numberOfGoogleMyBusinessPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfSeoAiAnalysisPerMonth"
                                label="Analyse IA/mois"
                                field-name="numberOfSeoAiAnalysisPerMonth"
                                field-id="numberOfSeoAiAnalysisPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> SEA </FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="
                                    form.numberOfSeaCampaignOptimizationPerMonth
                                "
                                label="Analyse et optimisation des campagnes/mois"
                                field-name="numberOfSeaCampaignOptimizationPerMonth"
                                field-id="numberOfSeaCampaignOptimizationPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="
                                    form.numberOfSeaGoogleAdsQualityPerMonth
                                "
                                label="Veille et optimisation qualité Google Ads/mois"
                                field-name="numberOfSeaGoogleAdsQualityPerMonth"
                                field-id="numberOfSeaGoogleAdsQualityPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="
                                    form.numberOfSeaMerchantCenterHealthPerMonth
                                "
                                label="Santé du compte Google Merchant Center/mois"
                                field-name="numberOfSeaMerchantCenterHealthPerMonth"
                                field-id="numberOfSeaMerchantCenterHealthPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfSeaAiAnalysisPerMonth"
                                label="Analyse IA/mois"
                                field-name="numberOfSeaAiAnalysisPerMonth"
                                field-id="numberOfSeaAiAnalysisPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> SMA </FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="
                                    form.numberOfSmaCampaignOptimizationPerMonth
                                "
                                label="Analyse et optimisation des campagnes/mois"
                                field-name="numberOfSmaCampaignOptimizationPerMonth"
                                field-id="numberOfSmaCampaignOptimizationPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfSmaAccountQualityPerMonth"
                                label="Qualité du compte et suivi technique/mois"
                                field-name="numberOfSmaAccountQualityPerMonth"
                                field-id="numberOfSmaAccountQualityPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="
                                    form.numberOfSmaShoppingFeedHealthPerMonth
                                "
                                label="Santé du flux shopping Meta/mois"
                                field-name="numberOfSmaShoppingFeedHealthPerMonth"
                                field-id="numberOfSmaShoppingFeedHealthPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfSmaAiAnalysisPerMonth"
                                label="Analyse IA/mois"
                                field-name="numberOfSmaAiAnalysisPerMonth"
                                field-id="numberOfSmaAiAnalysisPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> SMO </FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="
                                    form.numberOfEditorialFrameworkPerMonth
                                "
                                label="Trame éditoriale/mois"
                                field-name="numberOfEditorialFrameworkPerMonth"
                                field-id="numberOfEditorialFrameworkPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfSmoAiAnalysisPerMonth"
                                label="Analyse IA/mois"
                                field-name="numberOfSmoAiAnalysisPerMonth"
                                field-id="numberOfSmoAiAnalysisPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormTitle> SEM </FormTitle>

                        <FormGrid>
                            <InputField
                                v-model="form.numberOfBannersPerMonth"
                                label="Bannières/mois"
                                field-name="numberOfBannersPerMonth"
                                field-id="numberOfBannersPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="form.numberOfEmailingPerMonth"
                                label="Emailing/mois"
                                field-name="numberOfEmailingPerMonth"
                                field-id="numberOfEmailingPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />

                            <InputField
                                v-model="
                                    form.numberOfShortMessageServicesPerMonth
                                "
                                label="SMS/mois"
                                field-name="numberOfShortMessageServicesPerMonth"
                                field-id="numberOfShortMessageServicesPerMonth"
                                type="number"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #roadmap>
                <FormColumn>
                    <FormContent>
                        <SortableList
                            v-model="form.roadmapObjectives"
                            add-label="Ajouter un objectif"
                            empty-text="Aucun objectif pour le moment. Cliquez sur « Ajouter un objectif » pour commencer la feuille de route."
                            :new-item="
                                () => ({
                                    id: null,
                                    title: '',
                                    description: '',
                                    _uid: nextRoadmapUid(),
                                })
                            "
                        >
                            <template #default="{ item }">
                                <InputField
                                    v-model="item.title"
                                    :field-name="`roadmapObjectiveTitle_${item._uid}`"
                                    :field-id="`roadmapObjectiveTitle_${item._uid}`"
                                    type="text"
                                    label="Intitulé"
                                    required
                                />

                                <TextareaField
                                    v-model="item.description"
                                    :field-name="`roadmapObjectiveDescription_${item._uid}`"
                                    :field-id="`roadmapObjectiveDescription_${item._uid}`"
                                    :rows="3"
                                    label="Description"
                                    resize="vertical"
                                />
                            </template>
                        </SortableList>
                    </FormContent>
                </FormColumn>
            </template>

            <template #internalNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.internalNotes"
                            label="Notes internes"
                            field-name="internalNotes"
                            field-id="internalNotes"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #seoNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.seoNotesToDo"
                            label="À faire"
                            field-name="seoNotesToDo"
                            field-id="seoNotesToDo"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.seoNotesLastChanges"
                            label="Dernières modifications"
                            field-name="seoNotesLastChanges"
                            field-id="seoNotesLastChanges"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #seaNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.seaNotesToDo"
                            label="À faire"
                            field-name="seaNotesToDo"
                            field-id="seaNotesToDo"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.seaNotesLastChanges"
                            label="Dernières modifications"
                            field-name="seaNotesLastChanges"
                            field-id="seaNotesLastChanges"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.seaNotesOtherInformation"
                            label="Autres informations"
                            field-name="seaNotesOtherInformation"
                            field-id="seaNotesOtherInformation"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.seaNotesBudget"
                            label="Budget"
                            field-name="seaNotesBudget"
                            field-id="seaNotesBudget"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #smaNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.smaNotesToDo"
                            label="À faire"
                            field-name="smaNotesToDo"
                            field-id="smaNotesToDo"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.smaNotesLastChanges"
                            label="Dernières modifications"
                            field-name="smaNotesLastChanges"
                            field-id="smaNotesLastChanges"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #smoNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.smoNotesToDo"
                            label="À faire"
                            field-name="smoNotesToDo"
                            field-id="smoNotesToDo"
                            size="small"
                        />
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.smoNotesLastChanges"
                            label="Dernières modifications"
                            field-name="smoNotesLastChanges"
                            field-id="smoNotesLastChanges"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #display>
                <FormColumn>
                    <FormContent>
                        <FormTitle>Tâches</FormTitle>

                        <SwitchField
                            v-model="
                                form.displayTimeEveryMondayOfTheCurrentMonth
                            "
                            label="Afficher le temps chaque lundi du mois en cours dans l’onglet “Temps Apikom”"
                            field-name="displayTimeEveryMondayOfTheCurrentMonth"
                            field-id="displayTimeEveryMondayOfTheCurrentMonth"
                        />

                        <SwitchField
                            v-model="form.displayTimeOfTheCurrentMonth"
                            label="Afficher le temps du mois en cours"
                            field-name="displayTimeOfTheCurrentMonth"
                            field-id="displayTimeOfTheCurrentMonth"
                        />

                        <SwitchField
                            v-model="form.hasTenantTask"
                            label="A des tickets Apikom"
                            field-name="hasTenantTask"
                            field-id="hasTenantTask"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>Apikom</FormTitle>

                        <SwitchField
                            v-model="form.hasInvoicing"
                            label="A de la facturation"
                            field-name="hasInvoicing"
                            field-id="hasInvoicing"
                        />

                        <SwitchField
                            v-model="form.hasOpportunity"
                            label="A une opportunité"
                            field-name="hasOpportunity"
                            field-id="hasOpportunity"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>DEV</FormTitle>

                        <SwitchField
                            v-model="form.hasPageSpeed"
                            label="A du temps de chargement"
                            field-name="hasPageSpeed"
                            field-id="hasPageSpeed"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>SEA</FormTitle>

                        <SwitchField
                            v-model="form.hasGoogleAdsAudience"
                            label="A des audiences Google Ads"
                            field-name="hasGoogleAdsAudience"
                            field-id="hasGoogleAdsAudience"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>SMA</FormTitle>

                        <SwitchField
                            v-model="form.hasFacebookAdsAudience"
                            label="A des audiences Facebook Ads"
                            field-name="hasFacebookAdsAudience"
                            field-id="hasFacebookAdsAudience"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>SEO</FormTitle>

                        <SwitchField
                            v-model="form.hasApikomContent"
                            label="A du contenu Apikom"
                            field-name="hasApikomContent"
                            field-id="hasApikomContent"
                        />

                        <SwitchField
                            v-model="form.hasContentCategories"
                            label="A du contenu catégories"
                            field-name="hasContentCategories"
                            field-id="hasContentCategories"
                        />

                        <SwitchField
                            v-model="form.hasContentProducts"
                            label="A du contenu produits"
                            field-name="hasContentProducts"
                            field-id="hasContentProducts"
                        />

                        <SwitchField
                            v-model="form.hasIndexation"
                            label="A de l'indexation"
                            field-name="hasIndexation"
                            field-id="hasIndexation"
                        />

                        <SwitchField
                            v-model="form.hasNetlinking"
                            label="A du netlinking"
                            field-name="hasNetlinking"
                            field-id="hasNetlinking"
                        />

                        <SwitchField
                            v-model="form.hasPositionTracking"
                            label="A du suivi de positions"
                            field-name="hasPositionTracking"
                            field-id="hasPositionTracking"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>SEM</FormTitle>

                        <SwitchField
                            v-model="form.hasEmailing"
                            label="A de l'emailing"
                            field-name="hasEmailing"
                            field-id="hasEmailing"
                        />

                        <SwitchField
                            v-model="form.hasShortMessageService"
                            label="A du SMS"
                            field-name="hasShortMessageService"
                            field-id="hasShortMessageService"
                        />
                    </FormContent>

                    <FormContent>
                        <FormTitle>Modules</FormTitle>

                        <SwitchField
                            v-model="form.hasCompetitors"
                            label="A de la concurrences"
                            field-name="hasCompetitors"
                            field-id="hasCompetitors"
                        />

                        <SwitchField
                            v-model="form.hasEmailingAutomation"
                            label="A de l'emailing automatisé"
                            field-name="hasEmailingAutomation"
                            field-id="hasEmailingAutomation"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #modules>
                <FormColumn>
                    <FormContent>
                        <SwitchField
                            v-for="(module, key) in form.moduleTree"
                            :key="key"
                            v-model="module.enabled"
                            :label="
                                module.fullTitle +
                                (module.typeLabel
                                    ? ' - ' + module.typeLabel
                                    : '')
                            "
                            :field-name="'moduleTree[' + key + '].enabled'"
                            :field-id="'moduleTree[' + key + '].enabled'"
                            inline
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #alerts>
                <FormColumn>
                    <FormContent>
                        <FormGrid>
                            <InputField
                                v-model="
                                    form.alertCategoryWithLessThanXProductsInStock
                                "
                                label="Nombre de produits minimum en stock pour une catégorie"
                                field-name="alertCategoryWithLessThanXProductsInStock"
                                field-id="alertCategoryWithLessThanXProductsInStock"
                                type="number"
                                placeholder="5"
                                min="0"
                                step="1"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="tenant"
                    :url="
                        route(
                            'front.projectManagement.customer.deleteCustomer',
                            {
                                tenant: tenant.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.customer.contacts',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
